import { CheckOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Image, Row, Space, Table, Tag, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useState } from 'react'
import { ROLE, STATUS_JOB, STATUS_JOB_FINISHABLE_BY_ADMIN, isJobManagerRole, normalizeUrlString } from 'src/helpers/constants'
import { useStore } from 'src/hooks'

/**
 * Bang "Goi y link thiet ke" trong popup chi tiet viec — admin/supporter/designer thay.
 *
 * Sau khi tao viec, server lay anh dau tien cua viec tim anh tuong tu va luu 5
 * ket qua dau (bang job_image_suggestions). Bam "Chon" de them link folder vao
 * o URL ben tren (chua luu, luu bang "Luu URL"/Review/Dong viec nhu nhap tay).
 * Trang thai "Da gan" tinh theo o URL hien tai: xoa link khoi URL thi nut hien lai.
 * Tick nhieu dong roi bam "Gan cac link da chon" de gan nhieu link mot lan.
 * "Bo qua" goi y: admin/supporter lam duoc moi luc, designer lam duoc khi viec
 * dang thuc hien (cung dieu kien voi nut "Chon").
 *
 * Tim anh chay nen luc tao viec nen mo popup ngay co the chua co goi y — bam
 * "Tim lai" de goi lai.
 */

const SUGGESTION_STATUS = {
   PENDING: 0,
   APPLIED: 1,
   REJECTED: 2
}

const STATUS_TAG: Record<number, { color: string; text: string }> = {
   [SUGGESTION_STATUS.PENDING]: { color: 'gold', text: 'Chưa gắn' },
   [SUGGESTION_STATUS.APPLIED]: { color: 'green', text: 'Đã gắn' },
   [SUGGESTION_STATUS.REJECTED]: { color: 'default', text: 'Bỏ qua' }
}

const getSuggestionLink = (record: any) => record.folderUrl || record.driveUrl

const JobDetailSuggestion = observer(({ handleSave }: { handleSave?: (status: boolean) => void }) => {
   const JobDetailStore = useStore('JobDetailStore')
   const AuthStore = useStore('AuthStore')
   const role = AuthStore.data_role
   const { jobDetail } = JobDetailStore
   const jobId = jobDetail?.id
   const status = jobDetail?.status
   const isManager = isJobManagerRole(role)
   // Cung dieu kien sua URL voi JobDetailLink
   const canEditUrl =
      (role === ROLE.DESIGNER && status === STATUS_JOB.DOING) ||
      (isManager && STATUS_JOB_FINISHABLE_BY_ADMIN.includes(status))
   const currentUrls = normalizeUrlString(jobDetail?.url).split(', ').filter(Boolean)
   const [suggestions, setSuggestions] = useState<any[]>([])
   const [isLoading, setIsLoading] = useState(false)
   const [processingId, setProcessingId] = useState<number | null>(null)
   const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
   const [isSavingUrl, setIsSavingUrl] = useState(false)

   const load = useCallback(async () => {
      if (!jobId) return
      setIsLoading(true)
      try {
         const data = await JobDetailStore.getSuggestions(jobId)
         setSuggestions(Array.isArray(data) ? data : [])
      } finally {
         setIsLoading(false)
      }
   }, [jobId, JobDetailStore])

   useEffect(() => {
      load()
   }, [load])

   const handleRefresh = async () => {
      setIsLoading(true)
      try {
         const data = await JobDetailStore.refreshSuggestions(jobId)
         if (Array.isArray(data)) setSuggestions(data)
      } finally {
         setIsLoading(false)
      }
   }

   // Chon goi y -> gan link vao URL va luu luon (giong bam "Luu URL")
   const addLinksToUrl = async (records: any[]) => {
      const urlList = typeof jobDetail?.url === 'string' && jobDetail.url !== '' ? jobDetail.url.split(', ') : ['']
      let changed = false
      records.forEach((record) => {
         const link = getSuggestionLink(record)
         if (!link || urlList.some((item: string) => item.trim() === link)) return
         // Dien vao o URL trong neu co, khong thi them o moi
         const emptyIndex = urlList.findIndex((item: string) => !item.trim())
         if (emptyIndex >= 0) urlList[emptyIndex] = link
         else urlList.push(link)
         changed = true
      })
      if (!changed) return
      const urlStrings = normalizeUrlString(urlList.join(', '))
      JobDetailStore.setJobDetailUrl(urlStrings)
      setIsSavingUrl(true)
      try {
         await JobDetailStore.postDesignerUpdateURL(jobId, urlStrings)
         handleSave?.(true)
      } finally {
         setIsSavingUrl(false)
      }
   }

   const handleSelect = (record: any) => addLinksToUrl([record])

   const handleSelectMany = async () => {
      await addLinksToUrl(suggestions.filter((item) => selectedRowKeys.includes(item.id)))
      setSelectedRowKeys([])
   }

   const handleReject = async (record: any) => {
      setProcessingId(record.id)
      try {
         const data = await JobDetailStore.rejectSuggestion(record.id)
         if (data) setSuggestions(data)
      } finally {
         setProcessingId(null)
      }
   }

   const columns = [
      {
         title: '#',
         dataIndex: 'rank',
         key: 'rank',
         width: 40
      },
      {
         title: 'Ảnh',
         dataIndex: 'thumbnailUrl',
         key: 'thumbnailUrl',
         width: 110,
         render: (value: string, record: any) =>
            value ? (
               <Image
                  src={value}
                  alt={record.fileName}
                  width={96}
                  height={64}
                  style={{ objectFit: 'contain', background: '#f5f5f5', borderRadius: 4 }}
                  referrerPolicy="no-referrer"
               />
            ) : null
      },
      {
         title: 'Folder',
         dataIndex: 'folderName',
         key: 'folderName',
         render: (value: string, record: any) => (
            <div style={{ minWidth: 160 }}>
               {record.folderUrl ? (
                  <a href={record.folderUrl} target="_blank" rel="noopener noreferrer external">
                     {value || record.folderUrl}
                  </a>
               ) : (
                  value
               )}
               {record.fileName && (
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                     {record.driveUrl ? (
                        <a
                           href={record.driveUrl}
                           target="_blank"
                           rel="noopener noreferrer external"
                           style={{ color: '#8c8c8c' }}
                        >
                           {record.fileName}
                        </a>
                     ) : (
                        record.fileName
                     )}
                  </div>
               )}
            </div>
         )
      },
      {
         title: 'Độ khớp',
         dataIndex: 'score',
         key: 'score',
         width: 80,
         render: (value: number) => (value == null ? '' : `${(value * 100).toFixed(1)}%`)
      },
      {
         title: 'Trạng thái',
         dataIndex: 'status',
         key: 'status',
         width: 90,
         render: (value: number, record: any) => {
            const tag = currentUrls.includes(getSuggestionLink(record))
               ? STATUS_TAG[SUGGESTION_STATUS.APPLIED]
               : STATUS_TAG[value === SUGGESTION_STATUS.REJECTED ? value : SUGGESTION_STATUS.PENDING]
            return tag ? <Tag color={tag.color}>{tag.text}</Tag> : null
         }
      },
      {
         title: '',
         key: 'action',
         width: 110,
         render: (_: any, record: any) => {
            if (currentUrls.includes(getSuggestionLink(record))) return null
            // Designer bo qua duoc goi y cua viec minh dang lam, admin/supporter thi luon duoc
            const canReject = (isManager || canEditUrl) && record.status === SUGGESTION_STATUS.PENDING
            return (
               <Space size={4}>
                  {canEditUrl && (
                     <Tooltip title="Chọn link gắn vào URL">
                        <Button
                           type="primary"
                           size="small"
                           icon={<CheckOutlined />}
                           disabled={!getSuggestionLink(record) || isSavingUrl}
                           onClick={() => handleSelect(record)}
                        />
                     </Tooltip>
                  )}
                  {canReject && (
                     <Tooltip title="Bỏ qua">
                        <Button
                           size="small"
                           icon={<CloseOutlined />}
                           loading={processingId === record.id}
                           onClick={() => handleReject(record)}
                        />
                     </Tooltip>
                  )}
               </Space>
            )
         }
      }
   ]

   return (
      <>
         <Row className="gx-flex-row gx-align-items-center gx-justify-content-between gx-mx-1" wrap={false}>
            <h4 style={{ fontSize: 14, fontWeight: 600, lineHeight: '22px', color: '#272944', margin: 0 }}>
               Gợi ý link thiết kế
            </h4>
            <Space size={8}>
               {canEditUrl && selectedRowKeys.length > 0 && (
                  <Button
                     type="primary"
                     size="small"
                     icon={<CheckOutlined />}
                     loading={isSavingUrl}
                     onClick={handleSelectMany}
                  >
                     Gắn {selectedRowKeys.length} link đã chọn
                  </Button>
               )}
               {isManager && (
                  <Button size="small" icon={<ReloadOutlined />} loading={isLoading} onClick={handleRefresh}>
                     Tìm lại
                  </Button>
               )}
            </Space>
         </Row>
         <div className="gx-mx-1">
            <Table
               rowKey="id"
               size="small"
               columns={columns}
               dataSource={suggestions}
               rowSelection={
                  canEditUrl
                     ? {
                          selectedRowKeys,
                          onChange: (keys) => setSelectedRowKeys(keys),
                          // Link da nam trong URL hoac khong co link thi khong tick duoc
                          getCheckboxProps: (record: any) => {
                             const link = getSuggestionLink(record)
                             return { disabled: !link || currentUrls.includes(link) }
                          }
                       }
                     : undefined
               }
               loading={isLoading}
               pagination={false}
               scroll={{ x: 520 }}
               locale={{ emptyText: isManager ? 'Chưa có gợi ý. Bấm "Tìm lại" để tìm theo ảnh của việc.' : 'Chưa có gợi ý.' }}
            />
         </div>
      </>
   )
})

export default JobDetailSuggestion
